import { cn } from '@/lib/utils';

// SVG Loader Icon Component
const LoaderIcon = ({ size = 16, color = "currentColor" }) => (
  <svg
    height={size}
    strokeLinejoin="round"
    style={{ color: color }}
    viewBox="0 0 16 16"
    width={size}
  >
    <title>Loader</title>
    <g clipPath="url(#clip0_2393_1490)">
      <path d="M8 0V4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 16V12"
        opacity="0.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M3.29773 1.52783L5.64887 4.7639"
        opacity="0.9"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M12.7023 1.52783L10.3511 4.7639"
        opacity="0.1"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M12.7023 14.472L10.3511 11.236"
        opacity="0.4"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M3.29773 14.472L5.64887 11.236"
        opacity="0.6"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M15.6085 5.52783L11.8043 6.7639"
        opacity="0.2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M0.391602 10.472L4.19583 9.23598"
        opacity="0.7"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M15.6085 10.4722L11.8043 9.2361"
        opacity="0.3"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M0.391602 5.52783L4.19583 6.7639"
        opacity="0.8"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </g>
    <defs>
      <clipPath id="clip0_2393_1490">
        <rect fill="white" height="16" width="16" />
      </clipPath>
    </defs>
  </svg>
);

const ModernLoader = ({ 
  size = "default", 
  color = "hsl(227.08, 75%, 62.35%)",
  text = "Loading...",
  showText = true,
  type = "rings" // "rings" | "svg"
}) => {
  const sizeClasses = {
    small: "w-6 h-6",
    default: "w-8 h-8", 
    large: "w-12 h-12"
  };

  const svgSizes = {
    small: 16,
    default: 24,
    large: 32
  };

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      {type === "svg" ? (
        /* SVG Loader */
        (<div className={cn("inline-flex animate-spin items-center justify-center")}>
          <LoaderIcon size={svgSizes[size]} color={color} />
        </div>)
      ) : (
        /* Modern spinner with multiple rings */
        (<div className="relative">
          {/* Outer ring */}
          <div 
            className={`${sizeClasses[size]} rounded-full border-2 border-gray-200`}
            style={{ borderTopColor: color }}
          >
            <div 
              className={`${sizeClasses[size]} rounded-full border-2 border-transparent animate-spin`}
              style={{ borderTopColor: color, borderRightColor: color }}
            ></div>
          </div>
          {/* Inner ring */}
          <div 
            className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
              size === "large" ? "w-8 h-8" : size === "default" ? "w-5 h-5" : "w-3 h-3"
            } rounded-full border-2 border-gray-100 animate-spin`}
            style={{ 
              borderTopColor: color,
              animationDirection: "reverse",
              animationDuration: "0.8s"
            }}
          ></div>
        </div>)
      )}
      {/* Loading text */}
      {showText && (
        <div 
          className="text-sm font-medium animate-pulse"
          style={{ color }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

// Modern Modal Loader with backdrop
export const ModernModalLoader = ({ 
  isVisible = true,
  text = "Loading...",
  size = "large"
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
      
      {/* Loader container */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-sm w-full">
        <ModernLoader 
          size={size}
          text={text}
          showText={true}
        />
      </div>
    </div>
  );
};

// Pulsing dots loader
export const PulsingDotsLoader = ({ 
  color = "hsl(227.08, 75%, 62.35%)",
  size = "default"
}) => {
  const dotSize = size === "large" ? "w-3 h-3" : size === "small" ? "w-1.5 h-1.5" : "w-2 h-2";
  
  return (
    <div className="flex space-x-1">
      {[0, 1, 2].map((index) => (
        <div
          key={index}
          className={`${dotSize} rounded-full animate-pulse`}
          style={{ 
            backgroundColor: color,
            animationDelay: `${index * 0.2}s`,
            animationDuration: "1s"
          }}
        ></div>
      ))}
    </div>
  );
};

export default ModernLoader;
